import React, { useState, useRef } from 'react';
import type SwiperCore from 'swiper';
import type { NavigationOptions } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import '../../assets/css/theme.css';
import '../../assets/css/landing.css';

interface Testimonial {
  id: number;
  quote: string;
  author: string;
  label: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    quote: "More than expected — crazy soft, flexible and best fitted. The quality surpassed everything I anticipated.",
    author: "Priya S.",
    label: "Casual Wear",
    rating: 5,
  },
  {
    id: 2,
    quote: "Best fitted white denim shirt more than expected. Crazy soft, flexible, and the stitching is impeccable.",
    author: "Meera R.",
    label: "Street Style",
    rating: 5,
  },
  {
    id: 3,
    quote: "Flexible and comfortable, perfect for both casual outings and daily wear. Absolutely love the fabric.",
    author: "Ananya K.",
    label: "Urban Fashion",
    rating: 5,
  },
  {
    id: 4,
    quote: "Elegant drape, beautiful print, and delivered ahead of time. This is my go-to store from now on.",
    author: "Sonal M.",
    label: "Festive Collection",
    rating: 5,
  },
  {
    id: 5,
    quote: "The saree was exactly as described — vibrant colour, smooth texture, and the packaging was stunning.",
    author: "Kavita D.",
    label: "Saree Collection",
    rating: 5,
  },
];

const StarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.2l7.1-.6z" />
  </svg>
);

const Testimonials = () => {
  // Active index = the middle card of the 3 visible
  const [activeIdx, setActiveIdx] = useState<number>(1); // start: index 1 is the middle of [0,1,2]
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<SwiperCore | null>(null);

  const handleSlideChange = (swiper: SwiperCore) => {
    // realIndex = left-most visible slide; middle = realIndex + 1
    const middle = (swiper.realIndex + 1) % testimonials.length;
    setActiveIdx(middle);
  };

  return (
    <section className="ffh-t">
      <div className="ffh-container">

        {/* Header */}
        <div className="ffh-section-head">
          <p className="ffh-eyebrow ffh-eyebrow--light">Testimonials</p>
          <h2 className="ffh-title ffh-title--light">We Love Good <em>Compliments</em></h2>
          <p className="ffh-lead ffh-lead--light">What our customers are saying</p>
        </div>

        <div className="ffh-t-slider">
          {/* Controls – rendered BEFORE <Swiper> so the refs exist when Swiper inits; CSS `order` moves them below */}
          <div className="ffh-t-controls">
            <button ref={prevRef} type="button" className="ffh-round-btn ffh-round-btn--light" aria-label="Previous">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="ffh-dots ffh-dots--light">
              {testimonials.map((_, i) => (
                <span key={i} className={`ffh-dot${i === activeIdx ? ' is-active' : ''}`} />
              ))}
            </div>
            <button ref={nextRef} type="button" className="ffh-round-btn ffh-round-btn--light" aria-label="Next">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7 4L12 9L7 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <Swiper
            modules={[Navigation, Autoplay]}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              const nav = swiper.params.navigation as NavigationOptions;
              nav.prevEl = prevRef.current;
              nav.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }}
            onSlideChange={handleSlideChange}
            autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
            loop={true}
            centeredSlides={false}
            slidesPerView={3}
            spaceBetween={28}
            breakpoints={{
              0:    { slidesPerView: 1, spaceBetween: 16 },
              700:  { slidesPerView: 2, spaceBetween: 20 },
              1024: { slidesPerView: 3, spaceBetween: 28 },
            }}
            style={{ padding: '44px 6px 14px' }}
          >
            {testimonials.map((t, idx) => {
              const isActive = idx === activeIdx;
              return (
                <SwiperSlide key={t.id} style={{ height: 'auto' }}>
                  <div className={`ffh-t-card${isActive ? ' is-active' : ''}`}>
                    <div className="ffh-t-avatar">{t.author.charAt(0)}</div>

                    <div className="ffh-t-stars" aria-label={`${t.rating} out of 5 stars`}>
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <StarIcon key={i} />
                      ))}
                    </div>

                    <p className="ffh-t-quote">“{t.quote}”</p>

                    <div className="ffh-t-rule" />
                    <div className="ffh-t-author">{t.author}</div>
                    <div className="ffh-t-label">{t.label}</div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;